/**
 * 技术指标计算（主图叠加 + 副图）
 *
 * 全部为纯函数，无副作用、无状态，便于单测。
 * 约定：返回数组与输入等长，前置不足处填 `null` —— 图表库不接受 undefined。
 */

import type { Bar } from './api';

export type Series = (number | null)[];

/** 简单移动平均 */
export function sma(values: number[], n: number): Series {
  const out: Series = new Array(values.length).fill(null);
  let sum = 0;
  for (let i = 0; i < values.length; i++) {
    sum += values[i]!;
    if (i >= n) sum -= values[i - n]!;
    if (i >= n - 1) out[i] = sum / n;
  }
  return out;
}

/** 布林带：中轨=MA20，上下轨=±2σ（样本标准差，与通达信一致） */
export function bollinger(closes: number[], n = 20, mult = 2): {
  mid: Series; upper: Series; lower: Series;
} {
  const mid = sma(closes, n);
  const upper: Series = new Array(closes.length).fill(null);
  const lower: Series = new Array(closes.length).fill(null);

  for (let i = n - 1; i < closes.length; i++) {
    const win = closes.slice(i - n + 1, i + 1);
    const mean = mid[i]!;
    const variance = win.reduce((a, v) => a + (v - mean) ** 2, 0) / n;
    const sd = Math.sqrt(variance);
    upper[i] = mean + mult * sd;
    lower[i] = mean - mult * sd;
  }
  return { mid, upper, lower };
}

/** 多空线 BBI = (MA3+MA6+MA12+MA24)/4 */
export function bbi(closes: number[]): Series {
  const m3 = sma(closes, 3);
  const m6 = sma(closes, 6);
  const m12 = sma(closes, 12);
  const m24 = sma(closes, 24);
  return closes.map((_, i) =>
    m3[i] === null || m6[i] === null || m12[i] === null || m24[i] === null
      ? null
      : (m3[i]! + m6[i]! + m12[i]! + m24[i]!) / 4,
  );
}

/** 成交量 MA（副图叠加量均线） */
export function volMa(volumes: number[], n = 5 | 10): Series {
  return sma(volumes, n);
}

/** MACD(12,26,9)：DIF / DEA / 柱 */
export function macd(closes: number[], fast = 12, slow = 26, signal = 9): {
  dif: Series; dea: Series; hist: Series;
} {
  const ema = (values: number[], n: number): number[] => {
    const k = 2 / (n + 1);
    const out: number[] = [];
    let prev = values[0] ?? 0;
    for (const v of values) {
      prev = v * k + prev * (1 - k);
      out.push(prev);
    }
    return out;
  };

  const ef = ema(closes, fast);
  const es = ema(closes, slow);
  const dif = closes.map((_, i) => ef[i]! - es[i]!);

  const deaRaw = ema(dif, signal);
  const hist = dif.map((v, i) => (v - deaRaw[i]!) * 2);   // 国内习惯乘 2

  return { dif, dea: deaRaw, hist };
}

/** RSI(14)：国内常用 SMA 平滑（而非 Wilder） */
export function rsi(closes: number[], n = 14): Series {
  const out: Series = new Array(closes.length).fill(null);
  if (closes.length <= n) return out;

  let up = 0;
  let down = 0;
  for (let i = 1; i <= n; i++) {
    const d = closes[i]! - closes[i - 1]!;
    if (d >= 0) up += d; else down -= d;
  }
  up /= n; down /= n;
  out[n] = down === 0 ? 100 : 100 - 100 / (1 + up / down);

  for (let i = n + 1; i < closes.length; i++) {
    const d = closes[i]! - closes[i - 1]!;
    const gain = d > 0 ? d : 0;
    const loss = d < 0 ? -d : 0;
    up = (up * (n - 1) + gain) / n;
    down = (down * (n - 1) + loss) / n;
    out[i] = down === 0 ? 100 : 100 - 100 / (1 + up / down);
  }
  return out;
}

/** KDJ(9,3,3) */
export function kdj(bars: Bar[], n = 9): { k: Series; d: Series; j: Series } {
  const k: Series = new Array(bars.length).fill(null);
  const d: Series = new Array(bars.length).fill(null);
  const j: Series = new Array(bars.length).fill(null);

  let prevK = 50;
  let prevD = 50;

  for (let i = n - 1; i < bars.length; i++) {
    const win = bars.slice(i - n + 1, i + 1);
    const hi = Math.max(...win.map((b) => b.high));
    const lo = Math.min(...win.map((b) => b.low));
    const rsv = hi === lo ? 50 : ((bars[i]!.close - lo) / (hi - lo)) * 100;
    prevK = (2 * prevK + rsv) / 3;
    prevD = (2 * prevD + prevK) / 3;
    k[i] = prevK;
    d[i] = prevD;
    j[i] = 3 * prevK - 2 * prevD;
  }
  return { k, d, j };
}

/* ─────────────── 分时派生 ─────────────── */

/**
 * 分时横轴：**真实 epoch 秒**（docs/UI设计.md §4.1）。
 *
 * 用 `Date.UTC` 构造（而非本地时区）是刻意的：lightweight-charts 对数值 time
 * 一律按 UTC 解释，把北京挂钟时间当成 UTC 存放，标签才能显示正确的 HH:MM。
 */
export function minuteEpoch(dateISO: string, hhmm: string): number {
  const y = Number(dateISO.slice(0, 4));
  const mo = Number(dateISO.slice(5, 7));
  const d = Number(dateISO.slice(8, 10));
  const hh = Number(hhmm.slice(0, 2));
  const mm = Number(hhmm.slice(2, 4));
  return Math.floor(Date.UTC(y, mo - 1, d, hh, mm) / 1000);
}

/** epoch 秒 → 'HH:MM'（按 UTC 还原）。 */
export function formatHHMM(sec: number): string {
  const dd = new Date(sec * 1000);
  return `${String(dd.getUTCHours()).padStart(2, '0')}:${String(dd.getUTCMinutes()).padStart(2, '0')}`;
}

/** epoch 秒 → 'MM-DD'（按 UTC 还原）。 */
export function formatMonthDay(sec: number): string {
  const dd = new Date(sec * 1000);
  return `${String(dd.getUTCMonth() + 1).padStart(2, '0')}-${String(dd.getUTCDate()).padStart(2, '0')}`;
}

/** 交易市场：决定上午收盘与下午时段。 */
export type SessionMarket = 'CN' | 'HK';

interface SessionBounds {
  /** 上午收盘（分钟数） */
  morningEnd: number;
  /** 下午开盘（分钟数） */
  afternoonStart: number;
  /** 下午收盘（分钟数） */
  afternoonEnd: number;
}

/** A 股与港股时段（docs/REQUIREMENTS.md §F2.1）。 */
export const SESSIONS: Record<SessionMarket, SessionBounds> = {
  CN: { morningEnd: 11 * 60 + 30, afternoonStart: 13 * 60, afternoonEnd: 15 * 60 },
  HK: { morningEnd: 12 * 60, afternoonStart: 13 * 60, afternoonEnd: 16 * 60 },
};

const MORNING_START = 9 * 60 + 30;
/** 午休交界处的 1 秒防撞间隔（否则 11:30 与 13:00 落同一坐标 → 崩库）。 */
const JUNCTION_GUARD = 1;
/** 相邻交易日之间的横向留白，让多日一眼可辨（合成轴上并非真实时间）。 */
const DAY_GAP_SEC = 30 * 60;

/** 'HHMM' / 'HH:MM' 都能解析（内部键无冒号、展示字符串有冒号,容错两种）。 */
const hhmmToMin = (hhmm: string): number => {
  const t = hhmm.replace(':', '');
  return Number(t.slice(0, 2)) * 60 + Number(t.slice(2, 4));
};
const minToHHMM = (m: number): string =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
/** 无冒号 'HHMM'（合成轴数据键格式，与上游分时数据一致）。 */
const minToHHMM4 = (m: number): string =>
  `${String(Math.floor(m / 60)).padStart(2, '0')}${String(m % 60).padStart(2, '0')}`;

/** 'HHMM' → 会话内压缩偏移（秒）：移除午休；11:30 与 13:00 差 1 秒。 */
export function sessionOffset(hhmm: string, market: SessionMarket): number {
  const s = SESSIONS[market];
  const m = hhmmToMin(hhmm);
  if (m <= s.morningEnd) return (m - MORNING_START) * 60;
  return (m - MORNING_START) * 60 - (s.afternoonStart - s.morningEnd) * 60 + JUNCTION_GUARD;
}

/** 压缩偏移（秒）→ 真实 'HH:MM'。 */
export function offsetToHHMM(off: number, market: SessionMarket): string {
  const s = SESSIONS[market];
  const morningSec = (s.morningEnd - MORNING_START) * 60;
  const lunchSec = (s.afternoonStart - s.morningEnd) * 60;
  const realSec = off <= morningSec + JUNCTION_GUARD / 2 ? off : off + lunchSec - JUNCTION_GUARD;
  return minToHHMM(Math.round(MORNING_START + realSec / 60));
}

/** 一个交易日在合成轴上占用的长度（会话 + 日间留白）。 */
export function dayStride(market: SessionMarket): number {
  const s = SESSIONS[market];
  const session = (s.morningEnd - MORNING_START + s.afternoonEnd - s.afternoonStart) * 60 + JUNCTION_GUARD;
  return session + DAY_GAP_SEC;
}

/** 收盘时刻的 'HHMM'（合成轴 whitespace 锚点用）。 */
export function sessionCloseHHMM(market: SessionMarket): string {
  const e = SESSIONS[market].afternoonEnd;
  return `${String(Math.floor(e / 60)).padStart(2, '0')}${String(e % 60).padStart(2, '0')}`;
}

/**
 * 会话内每 30 分钟格点 + 收盘的 'HHMM' 升序清单（合成轴 whitespace 锚点用）。
 *
 * 库的 `timeToCoordinate` 只对 **series 里存在**的数据时间点返回坐标 ——
 * 盘中未走完的时段若不挂锚点，自绘轴的格线/刻度会拿 null 消失。
 * CN：10:00 10:30 11:30 13:00 13:30 14:00 14:30 15:00（交界与尾格必含）
 */

/**
 * 从 lastHHMM 的下一分钟起、到收盘的每分钟 'HHMM' 升序清单。
 * 跨午休自动衔接（11:31 后第一个键是 13:00）。lastHHMM=收盘 → 空数组。
 *
 * 用途：给分时序列尾部补 whitespace 锚点 —— 库对数据项**等宽排布**，
 * 未来时段若只有 30 分钟格点锚点，30 分钟会被压成一项宽、比例错乱；
 * 逐分钟锚点保证「剩余交易时间」的横向比例与已走时段一致。
 */
export function minutesAfterToClose(lastHHMM: string, market: SessionMarket): string[] {
  const s = SESSIONS[market];
  let m = hhmmToMin(lastHHMM) + 1;
  const out: string[] = [];
  while (m <= s.afternoonEnd) {
    if (m > s.morningEnd && m < s.afternoonStart) { m = s.afternoonStart; }
    out.push(minToHHMM4(m));
    m++;
  }
  return out;
}

/**
 * **合成时间轴**：把「午休」与「隔夜/假日」都从横轴移除 ——
 * 每个交易日的会话等宽、首尾相接（中间留 `DAY_GAP_SEC` 的视觉空隙）。
 *
 * 为什么连隔夜也压缩：若按真实日期铺开，国庆等长假会在轴上留下比日内宽几十倍
 * 的空白，且库默认只显示最近一段。合成轴让 5 天等宽并排，既连续又可读。
 *
 * `base` 取首个交易日 09:30 的真实 epoch 秒，仅作数值锚点（不再代表真实时刻）。
 */
export function axisBase(dateISO: string): number {
  return minuteEpoch(dateISO, '0930');
}

/** 第 dayIndex 天某时刻在合成轴上的坐标（秒）。 */
export function axisTime(base: number, dayIndex: number, hhmm: string, market: SessionMarket): number {
  return base + dayIndex * dayStride(market) + sessionOffset(hhmm, market);
}

/** 合成坐标 → 第几天与真实 'HH:MM'（十字线用）。 */
export function decodeAxisTime(
  axisSec: number, base: number, market: SessionMarket,
): { dayIndex: number; hhmm: string } {
  const stride = dayStride(market);
  const dayIndex = Math.floor((axisSec - base) / stride);
  const off = axisSec - base - dayIndex * stride;
  return { dayIndex, hhmm: offsetToHHMM(off, market) };
}

export interface AxisMark {
  /** 合成轴坐标（与数据点一致，可直接用 timeToCoordinate） */
  time: number;
  label: string;
  /** 是否为该日的日期标签（置顶于会话首，多日模式用） */
  isDate?: boolean;
}

/**
 * 生成第 dayIndex 天的刻度。`stepMin` 为压缩后的分钟间隔；午休交界处
 * 合并显示 `11:30/13:00`（港股 `12:00/13:00`）。`withDate` 时在会话首插日期。
 */
export function axisMarks(
  base: number, dayIndex: number, dateISO: string, market: SessionMarket, stepMin: number, withDate: boolean,
): AxisMark[] {
  const s = SESSIONS[market];
  const start = base + dayIndex * dayStride(market);
  const morningLen = s.morningEnd - MORNING_START;
  const total = morningLen + (s.afternoonEnd - s.afternoonStart);
  const out: AxisMark[] = [];
  if (withDate) out.push({ time: start, label: dateISO.slice(5), isDate: true });

  // 交界（上午收盘/下午开盘）与收盘日终必须出现，即使不在 step 网格上（港股午休 150 分钟）
  const cms = new Set<number>();
  for (let cm = 0; cm <= total; cm += stepMin) cms.add(cm);
  cms.add(morningLen);
  cms.add(total);

  for (const cm of [...cms].sort((a, b) => a - b)) {
    if (withDate && cm === 0) continue; // 日期标签已占据会话首
    const off = cm * 60 + (cm > morningLen ? JUNCTION_GUARD : 0);
    const label = cm === morningLen
      ? `${minToHHMM(s.morningEnd)}/${minToHHMM(s.afternoonStart)}`
      : offsetToHHMM(cm * 60, market);
    out.push({ time: start + off, label });
  }
  return out;
}

/**
 * 累计成交量 → 均价线。
 *
 * ⚠️ **单位陷阱（已实测确认）**：腾讯分时接口的
 *   `cumVolume` 单位是**手**（1 手 = 100 股），
 *   而 `cumAmount` 单位是**元**。
 *   直接 amount/volume 会得到约 1640 而不是 3840（实测上证 1500 点）：
 *
 *     cumAmount = 679,398,992,444 元
 *     cumVolume =     414,560,247 手 = 41,456,024,700 股
 *     均价 = 679398992444 / 414560247   = 1638.84   ← 错
 *     均价 = 679398992444 / 41456024700 = 16.39    ← 仍错（差 100）
 *
 *   正确的做法不是猜倍数，而是**用成交量反推指数点位不可靠** ——
 *   指数的「成交量」是成分股加总，与指数点位没有稳定的换算关系。
 *
 *   故此处按需求 §3.5.5 的降级策略处理：
 *   `cumAmount` 与 `cumVolume` 单位不一致时，均价线**退化为价格线**并由 UI 标注，
 *   而不是画一条数学上错误的白线（错线比无线更有害 —— 用户会当真）。
 *
 * @param volumeUnit 该源的 cumVolume 单位，'share' 表示已是股
 */
export function averageLine(
  points: { price: number; cumVolume: number; cumAmount: number }[],
  opts: { volumeUnit?: 'lot' | 'share' } = {},
): Series {
  const { volumeUnit = 'share' } = opts;
  return points.map((p) => {
    // 单位为「手」时无法可靠换算成均价 → 退化为价格线
    if (volumeUnit === 'lot') return p.price;
    if (p.cumAmount > 0 && p.cumVolume > 0) return p.cumAmount / p.cumVolume;
    return p.price;
  });
}
